// components/column-manager/column-manager.tsx
import React, { useState, useMemo } from 'react'
import { Button, Drawer, Checkbox, Space, Input, Divider, Tooltip } from 'antd'
import { SettingOutlined, SearchOutlined } from '@ant-design/icons'
import type { ColumnSetting } from '../../hooks/use-collum-manager'

interface ColumnManagerProps {
    columnSettings: ColumnSetting[]
    onToggleColumn: (columnKey: string) => void
    onShowAll?: () => void
    onHideAll?: () => void
    onReset?: () => void
}

// Рекурсивная функция для подсчета всех видимых столбцов (включая вложенные)
const countVisibleColumns = (settings: ColumnSetting[]): number => {
    let count = 0
    settings.forEach(setting => {
        if (setting.visible && !setting.isGroup) {
            count++
        }
        if (setting.children) {
            count += countVisibleColumns(setting.children)
        }
    })
    return count
}

// Рекурсивная функция для подсчета всех столбцов (включая вложенные, но не группы)
const countTotalColumns = (settings: ColumnSetting[]): number => {
    let count = 0
    settings.forEach(setting => {
        if (!setting.isGroup) {
            count++
        }
        if (setting.children) {
            count += countTotalColumns(setting.children)
        }
    })
    return count
}

// Рекурсивная функция для фильтрации столбцов по поисковому запросу
const filterColumns = (settings: ColumnSetting[], searchLower: string): ColumnSetting[] => {
    return settings
        .map(setting => {
            const titleMatches = setting.title.toLowerCase().includes(searchLower)
            const hasChildren = setting.children && setting.children.length > 0

            if (hasChildren) {
                // Для групп: фильтруем дочерние элементы
                const filteredChildren = filterColumns(setting.children!, searchLower)
                // Если название группы совпадает или есть совпадающие дочерние элементы
                if (titleMatches || filteredChildren.length > 0) {
                    return {
                        ...setting,
                        children: filteredChildren,
                    }
                }
                return null
            } else {
                // Для обычных столбцов: проверяем только название
                return titleMatches ? setting : null
            }
        })
        .filter((setting): setting is ColumnSetting => setting !== null)
}

export const ColumnManager: React.FC<ColumnManagerProps> = ({
    columnSettings,
    onToggleColumn,
    onShowAll,
    onHideAll,
    onReset,
}) => {
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [searchText, setSearchText] = useState('')

    const visibleCount = useMemo(() => countVisibleColumns(columnSettings), [columnSettings])
    const totalCount = useMemo(() => countTotalColumns(columnSettings), [columnSettings])

    // Фильтруем столбцы по поисковому запросу
    const filteredColumns = useMemo(() => {
        if (!searchText.trim()) {
            return columnSettings
        }
        const searchLower = searchText.toLowerCase()
        return filterColumns(columnSettings, searchLower)
    }, [columnSettings, searchText])

    const handleToggle = (columnKey: string) => {
        onToggleColumn(columnKey)
    }

    const handleShowAll = () => {
        onShowAll?.()
    }

    const handleHideAll = () => {
        onHideAll?.()
    }

    const handleReset = () => {
        onReset?.()
    }

    return (
        <>
            <Tooltip title='Настройки колонок'>
                <Button icon={<SettingOutlined />} onClick={() => setDrawerOpen(true)}>
                    Колонки ({visibleCount}/{totalCount})
                </Button>
            </Tooltip>

            <Drawer
                title='Настройка видимости колонок'
                placement='right'
                width={400}
                open={drawerOpen}
                onClose={() => {
                    setDrawerOpen(false)
                    setSearchText('') // Очищаем поиск при закрытии
                }}
                extra={
                    <Space>
                        <span style={{ fontSize: 14, color: '#666' }}>
                            {visibleCount}/{totalCount}
                        </span>
                    </Space>
                }
            >
                <Space direction='vertical' style={{ width: '100%' }} size='middle'>
                    {/* Поиск */}
                    <Input
                        placeholder='Поиск колонок...'
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        allowClear
                    />

                    {/* Кнопки действий */}
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Button size='small' onClick={handleShowAll}>
                            Показать все
                        </Button>
                        <Button size='small' onClick={handleHideAll}>
                            Скрыть все
                        </Button>
                        <Button size='small' onClick={handleReset}>
                            Сбросить
                        </Button>
                    </Space>

                    <Divider style={{ margin: '8px 0' }} />

                    {/* Список колонок */}
                    <div
                        style={{
                            maxHeight: 'calc(100vh - 180px)',
                            overflowY: 'auto',
                            paddingRight: 8,
                        }}
                    >
                        {filteredColumns.length === 0 ? (
                            <div style={{ textAlign: 'center', color: '#999', padding: '20px 0' }}>
                                Колонки не найдены
                            </div>
                        ) : (
                            <ColumnList
                                settings={filteredColumns}
                                onToggle={handleToggle}
                                level={0}
                            />
                        )}
                    </div>
                </Space>
            </Drawer>
        </>
    )
}

// Компонент для рекурсивного отображения списка колонок с иерархией
interface ColumnListProps {
    settings: ColumnSetting[]
    onToggle: (columnKey: string) => void
    level: number
}

const ColumnList: React.FC<ColumnListProps> = ({ settings, onToggle, level }) => {
    return (
        <Space direction='vertical' style={{ width: '100%' }} size='small'>
            {settings.map(setting => {
                const hasChildren = setting.children && setting.children.length > 0

                return (
                    <div key={setting.key}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                padding: '8px 12px',
                                paddingLeft: `${12 + level * 24}px`,
                                borderRadius: 4,
                                cursor: 'pointer',
                                transition: 'background-color 0.2s',
                            }}
                            onClick={() => onToggle(setting.key)}
                            onMouseEnter={e => {
                                e.currentTarget.style.backgroundColor = '#f5f5f5'
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.backgroundColor = 'transparent'
                            }}
                        >
                            <Checkbox
                                checked={setting.visible}
                                onChange={() => onToggle(setting.key)}
                                onClick={e => e.stopPropagation()}
                            />
                            <span
                                style={{
                                    flex: 1,
                                    userSelect: 'none',
                                    color: setting.isGroup ? '#1890ff' : 'inherit',
                                }}
                            >
                                {setting.title}
                            </span>
                        </div>
                        {/* Рекурсивно отображаем дочерние элементы */}
                        {hasChildren && (
                            <ColumnList
                                settings={setting.children!}
                                onToggle={onToggle}
                                level={level + 1}
                            />
                        )}
                    </div>
                )
            })}
        </Space>
    )
}
